import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const VoterSurveyForm = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Context
    const { appId, userId, userType, language } = useAppId();

    const voter = location.state?.voter || {};

    const lang = language || 'en';

    // Translation Dictionary
    const t = {
        en: {
            pageTitle: "Voter Survey",
            voterName: "Voter Name",
            idCard: "ID Card",
            boothNo: "Booth No",
            address: "Address",
            location: "Location",
            checkingGPS: "Checking GPS...",
            fetchingGPS: "Fetching GPS location...",
            gpsOff: "GPS is off or permission not granted.",
            gpsNotSupported: "Your device does not support GPS.",
            refresh: "Refresh",
            surveyInfo: "Survey Information",
            available: "Available",
            notAvailable: "Not Available",
            voterOpinion: "Voter Opinion",
            positive: "Positive",
            negative: "Negative",
            doubtful: "Doubtful",
            cantSay: "Can't Say",
            mobileNumber: "Mobile Number (Optional)",
            enterMobile: "Enter 10-digit number if available",
            note: "Note / Comment",
            enterNote: "Write any additional notes here...",
            selectReason: "Select reason for absence...",
            reason: "Select Reason",
            shiftedOut: "Shifted Out",
            death: "Death",
            wrongMobile: "Wrong Mobile No",
            callNotReceived: "Call Not Received",
            saving: "Saving...",
            saveSurvey: "Save Survey",
            successMessage: "Survey saved successfully!",
            errorMessage: "Failed to save survey. Please try again.",
            selectOpinion: "Please select voter opinion (Positive/Negative etc.).",
            selectReasonMsg: "Please select reason for absence.",
            surveyError: "Error saving survey: "
        },
        hi: {
            pageTitle: "मतदाता सर्वेक्षण",
            voterName: "मतदाता का नाम",
            idCard: "आईडी कार्ड",
            boothNo: "बूथ नं",
            address: "पता",
            location: "स्थान",
            checkingGPS: "जीपीएस चेक कर रहे हैं...",
            fetchingGPS: "जीपीएस स्थान प्राप्त कर रहे हैं...",
            gpsOff: "जीपीएस बंद है या अनुमति नहीं दी गई है।",
            gpsNotSupported: "आपका डिवाइस जीपीएस का समर्थन नहीं करता है।",
            refresh: "ताज़ा करें",
            surveyInfo: "सर्वेक्षण जानकारी",
            available: "उपलब्ध",
            notAvailable: "अनुपलब्ध",
            voterOpinion: "मतदाता की राय",
            positive: "सकारात्मक",
            negative: "नकारात्मक",
            doubtful: "संदिग्ध",
            cantSay: "कुछ नहीं कह सकते",
            mobileNumber: "मोबाइल नंबर (वैकल्पिक)",
            enterMobile: "यदि उपलब्ध हो तो 10 अंकों का नंबर दर्ज करें",
            note: "नोट / टिप्पणी",
            enterNote: "यहां कोई अन्य विवरण लिखें...",
            selectReason: "अनुपलब्ध होने का कारण चुनें...",
            reason: "कारण चुनें",
            shiftedOut: "स्थानांतरित",
            death: "मृत्यु",
            wrongMobile: "गलत मोबाइल नंबर",
            callNotReceived: "कॉल प्राप्त नहीं हुआ",
            saving: "सेव हो रहा है...",
            saveSurvey: "सर्वेक्षण सेव करें",
            successMessage: "सर्वेक्षण सफलतापूर्वक सेव हो गया!",
            errorMessage: "सर्वेक्षण सेव करने में विफल। कृपया पुनः प्रयास करें।",
            selectOpinion: "कृपया मतदाता की राय (सकारात्मक/नकारात्मक आदि) चुनें।",
            selectReasonMsg: "कृपया अनुपलब्ध होने का कारण चुनें।",
            surveyError: "सर्वेक्षण सेव करने में त्रुटि: "
        },
        gu: {
            pageTitle: "વોટર સર્વે",
            voterName: "મતદારનું નામ",
            idCard: "આઈડી કાર્ડ",
            boothNo: "બૂથ નં",
            address: "સરનામું",
            location: "લોકેશન",
            checkingGPS: "GPS ચેક કરી રહ્યા છીએ...",
            fetchingGPS: "GPS લોકેશન લાવી રહ્યા છીએ...",
            gpsOff: "GPS બંધ છે અથવા પરવાનગી નથી.",
            gpsNotSupported: "તમારા ડિવાઇસમાં GPS સપોર્ટ નથી.",
            refresh: "રીફ્રેશ",
            surveyInfo: "સર્વે માહિતી",
            available: "ઉપલબ્ધ",
            notAvailable: "ઉપલબ્ધ નથી",
            voterOpinion: "મતદારનો અભિપ્રાય",
            positive: "પોઝિટિવ",
            negative: "નેગેટિવ",
            doubtful: "ડાઉટફુલ",
            cantSay: "કંઈ નહીં",
            mobileNumber: "મોબાઈલ નંબર (ઐચ્છિક)",
            enterMobile: "જો હોય તો ૧૦ આંકડાનો નંબર",
            note: "નોંધ / ટિપ્પણી",
            enterNote: "અહીં અન્ય કોઈ પણ વિગતવાર નોંધ લખો...",
            selectReason: "ગેરહાજર રહેવાનું કારણ પસંદ કરો...",
            reason: "કારણ પસંદ કરો",
            shiftedOut: "Shifted Out",
            death: "Death",
            wrongMobile: "Wrong Mobile No",
            callNotReceived: "Call Not Received",
            saving: "સેવ થઈ રહ્યું છે...",
            saveSurvey: "સર્વે સેવ કરો",
            successMessage: "સર્વે સફળતાપૂર્વક સેવ થઈ ગયો છે!",
            errorMessage: "સર્વે સેવ કરવામાં નિષ્ફળ. કૃપા કરીને ફરી પ્રયાસ કરો.",
            selectOpinion: "કૃપા કરીને મતદારનો અભિપ્રાય (પોઝિટિવ/નેગેટિવ વગેરે) પસંદ કરો.",
            selectReasonMsg: "કૃપા કરીને ગેરહાજર રહેવાનું કારણ પસંદ કરો.",
            surveyError: "સર્વે સેવ કરવામાં ભૂલ: "
        }
    };

    const txt = t[lang] || t.gu;

    // 💡 નોંધ: નામ માટે મેં થોડો સુધારો કર્યો છે જેથી ફર્સ્ટ નેમ અને અટક યોગ્ય રીતે જોડાય
    const voterName = voter.eng_f_name ? `${voter.eng_f_name} ${voter.eng_surname}` : "";
    const voterId = voter.idcard_no || "";
    const boothNo = voter.booth_no || voter.part_no || "";
    const address = voter.eng_localityid || "";
    // Form States
    const [isAvailable, setIsAvailable] = useState(true);
    const [voterStatus, setVoterStatus] = useState(''); // 'P', 'N', 'D', 'C'
    const [mobileNo, setMobileNo] = useState('');
    const [note, setNote] = useState('');
    const [reason, setReason] = useState('');

    // Location States
    const [latLong, setLatLong] = useState('');
    const [locationText, setLocationText] = useState(txt.checkingGPS);
    const [isGpsOn, setIsGpsOn] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reasons List
    const reasonsList = [
        txt.shiftedOut,
        txt.death,
        txt.wrongMobile,
        txt.callNotReceived
    ];

    // Fetch Location Function
    const fetchLocation = () => {
        setLocationText(txt.fetchingGPS);
        setIsGpsOn(false);
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLatLong(`${position.coords.latitude},${position.coords.longitude}`);
                    setLocationText(`${position.coords.latitude.toFixed(5)}, ${position.coords.longitude.toFixed(5)}`);
                    setIsGpsOn(true);
                },
                (error) => {
                    console.error("Location error:", error);
                    setLatLong('');
                    setLocationText(txt.gpsOff);
                    setIsGpsOn(false);
                },
                { enableHighAccuracy: true, timeout: 5000 }
            );
        } else {
            setLocationText(txt.gpsNotSupported);
        }
    };

    useEffect(() => {
        if (!appId) {
            navigate('/login');
            return;
        }
        fetchLocation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const handleSubmit = async () => {
        if (isAvailable && !voterStatus) {
            alert(txt.selectOpinion);
            return;
        }
        if (!isAvailable && !reason) {
            alert(txt.selectReasonMsg);
            return;
        }

        setIsSubmitting(true);

        const payload = {
            app_id: appId,
            voter_idcard: voterId,
            booth_no: boothNo,
            survey_by: userId,
            survey_by_designation: userType,
            voter_available: isAvailable ? "true" : "false",
            not_available_reason: isAvailable ? "" : reason,
            not_available_note: isAvailable ? "" : note,
            contact_no: isAvailable ? mobileNo : "",
            voter_status: isAvailable ? voterStatus : "",
            note: isAvailable ? note : "",
            lat_long: latLong,
            visit_location: locationText // Passing what we fetched or GPS off status
        };

        try {
            const response = await axios.post(`${BASE_URL}/api/ins_voter_survey`, payload);

            if (response.data.success || response.data.Success === "1") {
                alert(txt.successMessage);
                sessionStorage.setItem('boothDetailActiveTab', 'voters'); // FIX: Ensures the booth page opens on the voters tab
                navigate(-1); // Go back after success
            } else {
                alert(txt.surveyError + JSON.stringify(response.data));
            }
        } catch (error) {
            console.error("Survey Submit Error:", error);
            alert(txt.errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="survey-page-container">
            {/* Header */}
            <div className="header-wrapper shadow-sm">
                <Header title={txt.pageTitle} />
            </div>

            <div className="content-container container-fluid p-3 p-md-4 mx-auto" style={{ maxWidth: '600px' }}>

                {/* Voter Info Card */}
                <div className="voter-info-card slide-up">
                    <h3 className="voter-name">{voterName}</h3>
                    <div className="divider"></div>

                    <div className="info-row">
                        <div className="icon-box"><svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M10 2v4a2 2 0 002 2h4m-6-6H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" /></svg></div>
                        <span className="info-label">{txt.idCard}</span>
                        <span className="info-colon">:</span>
                        <span className="info-value fw-bold text-dark">{voterId}</span>
                    </div>

                    <div className="info-row">
                        <div className="icon-box"><svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg></div>
                        <span className="info-label">{txt.boothNo}</span>
                        <span className="info-colon">:</span>
                        <span className="info-value fw-bold text-dark">{boothNo}</span>
                    </div>

                    <div className="info-row align-items-start">
                        <div className="icon-box mt-1"><svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg></div>
                        <span className="info-label mt-1">{txt.address}</span>
                        <span className="info-colon mt-1">:</span>
                        <span className="info-value text-dark" style={{ lineHeight: '1.4' }}>{address}</span>
                    </div>
                </div>

                {/* Location Section */}
                <h4 className="section-title mt-4">{txt.location}</h4>
                <div className="location-box slide-up" style={{ animationDelay: '0.1s' }}>
                    <div className={`gps-icon-circle ${isGpsOn ? 'bg-blue' : 'bg-gray'}`}>
                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2" /></svg>
                    </div>
                    <div className="gps-text flex-grow-1">{locationText}</div>
                    <button className="refresh-btn" onClick={fetchLocation}>
                        <svg width="20" height="20" fill="none" stroke="#3b82f6" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                    </button>
                </div>

                {/* Survey Form Section */}
                <h4 className="section-title mt-4">{txt.surveyInfo}</h4>
                <div className="survey-card slide-up" style={{ animationDelay: '0.2s' }}>

                    {/* Toggle Switch */}
                    <div className="toggle-wrapper mb-4">
                        <div
                            className={`toggle-option ${isAvailable ? 'active' : ''}`}
                            onClick={() => setIsAvailable(true)}
                        >
                            {txt.available}
                        </div>
                        <div
                            className={`toggle-option ${!isAvailable ? 'active text-muted' : 'text-muted'}`}
                            onClick={() => setIsAvailable(false)}
                            style={!isAvailable ? { backgroundColor: '#3b82f6', color: 'white' } : {}}
                        >
                            {txt.notAvailable}
                        </div>
                    </div>

                    {/* --- IF AVAILABLE (ઉપલબ્ધ) --- */}
                    {isAvailable && (
                        <div className="fade-in">
                            <h5 className="input-label">{txt.voterOpinion}</h5>
                            <div className="opinion-grid mb-4">
                                {/* Positive */}
                                <div className={`opinion-box ${voterStatus === 'P' ? 'active-p' : ''}`} onClick={() => setVoterStatus('P')}>
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={voterStatus === 'P' ? '#22c55e' : '#10b981'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M8 14s1.5 2 4 2 4-2 4-2"></path><line x1="9" y1="9" x2="9.01" y2="9"></line><line x1="15" y1="9" x2="15.01" y2="9"></line></svg>
                                    <span className={voterStatus === 'P' ? 'text-success' : 'text-muted'}>{txt.positive}</span>
                                </div>
                                {/* Negative */}
                                <div className={`opinion-box ${voterStatus === 'N' ? 'active-n' : ''}`} onClick={() => setVoterStatus('N')}>
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={voterStatus === 'N' ? '#ef4444' : '#ef4444'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M16 16s-1.5-2-4-2-4 2-4 2"></path><line x1="9" y1="9" x2="9.01" y2="9"></line><line x1="15" y1="9" x2="15.01" y2="9"></line></svg>
                                    <span className={voterStatus === 'N' ? 'text-danger' : 'text-muted'}>{txt.negative}</span>
                                </div>
                                {/* Doubtful */}
                                <div className={`opinion-box ${voterStatus === 'D' ? 'active-d' : ''}`} onClick={() => setVoterStatus('D')}>
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={voterStatus === 'D' ? '#f59e0b' : '#f59e0b'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="8" y1="15" x2="16" y2="15"></line><line x1="9" y1="9" x2="9.01" y2="9"></line><line x1="15" y1="9" x2="15.01" y2="9"></line></svg>
                                    <span className={voterStatus === 'D' ? 'text-warning' : 'text-muted'}>{txt.doubtful}</span>
                                </div>
                                {/* Can't Say */}
                                <div className={`opinion-box ${voterStatus === 'C' ? 'active-c' : ''}`} onClick={() => setVoterStatus('C')}>
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={voterStatus === 'C' ? '#64748b' : '#64748b'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                                    <span className={voterStatus === 'C' ? 'text-secondary' : 'text-muted'}>{txt.cantSay}</span>
                                </div>
                            </div>

                            <h5 className="input-label">{txt.mobileNumber}</h5>
                            <div className="mobile-input-wrapper mb-4">
                                <div className="prefix-box">
                                    <svg width="18" height="18" fill="none" stroke="#1e293b" strokeWidth="2" viewBox="0 0 24 24"><rect x="5" y="2" width="14" height="20" rx="2" ry="2" /><line x1="12" y1="18" x2="12.01" y2="18" /></svg>
                                    <span className="fw-bold ms-1">+91</span>
                                </div>
                                <input
                                    type="text"
                                    className="form-control border-0 bg-transparent shadow-none"
                                    placeholder={txt.enterMobile}
                                    maxLength="10"
                                    value={mobileNo}
                                    onChange={(e) => setMobileNo(e.target.value.replace(/[^0-9]/g, ''))}
                                />
                            </div>

                            <h5 className="input-label">{txt.note}</h5>
                            <textarea
                                className="custom-textarea form-control"
                                placeholder={txt.enterNote}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                            ></textarea>
                        </div>
                    )}

                    {/* --- IF NOT AVAILABLE (ઉપલબ્ધ નથી) --- */}
                    {!isAvailable && (
                        <div className="fade-in">
                            <h5 className="input-label">{txt.reason}</h5>
                            <select
                                className="custom-select form-control mb-4"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            >
                                <option value="">{txt.selectReason}</option>
                                {reasonsList.map((r, i) => <option key={i} value={r}>{r}</option>)}
                            </select>

                            <h5 className="input-label">{txt.note}</h5>
                            <textarea
                                className="custom-textarea form-control"
                                placeholder={txt.enterNote}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                            ></textarea>
                        </div>
                    )}
                </div>

                {/* Save Button */}
                <button
                    className="btn-save-survey mt-4"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? txt.saving : txt.saveSurvey}
                </button>

            </div>

            <style jsx>{`
                .survey-page-container { width: 100%; min-height: 100vh; background: #eef2f6; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding-bottom: 40px; }
                .header-wrapper { background: white; padding: 0 24px; position: sticky; top: 0; z-index: 50; }

                /* Voter Card */
                .voter-info-card { background: white; border-radius: 20px; padding: 24px; border: 1px solid #1e293b; box-shadow: 0 4px 10px rgba(0,0,0,0.03); margin-top: 10px; }
                .voter-name { font-size: 18px; font-weight: 800; color: #1e3a8a; margin: 0; }
                .divider { height: 1px; background: #e2e8f0; margin: 16px 0; }
                .info-row { display: flex; align-items: center; margin-bottom: 12px; font-size: 14px; }
                .info-row:last-child { margin-bottom: 0; }
                .icon-box { color: #3b82f6; width: 24px; }
                .info-label { width: 80px; color: #64748b; font-weight: 500; }
                .info-colon { margin: 0 10px; color: #64748b; }
                .info-value { flex: 1; }

                .section-title { font-size: 16px; font-weight: 800; color: #1e293b; margin-bottom: 12px; margin-left: 4px; }

                /* Location Box */
                .location-box { background: white; border-radius: 16px; padding: 16px 20px; display: flex; align-items: center; gap: 16px; box-shadow: 0 2px 6px rgba(0,0,0,0.02); }
                .gps-icon-circle { width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #3b82f6; }
                .bg-blue { background: #eff6ff; }
                .bg-gray { background: #f1f5f9; color: #94a3b8; }
                .gps-text { font-size: 14px; font-weight: 500; color: #1e293b; }
                .refresh-btn { background: none; border: none; padding: 5px; cursor: pointer; transition: transform 0.2s; }
                .refresh-btn:hover { transform: rotate(180deg); }

                /* Survey Card */
                .survey-card { background: white; border-radius: 20px; padding: 24px; box-shadow: 0 4px 10px rgba(0,0,0,0.03); }
                
                /* Toggle Switch */
                .toggle-wrapper { display: flex; background: #e2e8f0; border-radius: 12px; overflow: hidden; padding: 4px; }
                .toggle-option { flex: 1; text-align: center; padding: 12px; font-size: 14px; font-weight: 700; border-radius: 8px; cursor: pointer; transition: all 0.3s; }
                .toggle-option.active { background: #3b82f6; color: white; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
                
                /* Opinion Grid */
                .input-label { font-size: 14px; font-weight: 800; color: #1e293b; margin-bottom: 12px; }
                .opinion-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
                .opinion-box { border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px 8px; display: flex; flex-direction: column; align-items: center; gap: 10px; cursor: pointer; transition: all 0.2s; background: white; }
                .opinion-box span { font-size: 11px; font-weight: 700; }
                .opinion-box.active-p { border-color: #22c55e; background: #f0fdf4; box-shadow: 0 4px 10px rgba(34, 197, 94, 0.15); transform: translateY(-3px); }
                .opinion-box.active-n { border-color: #ef4444; background: #fef2f2; box-shadow: 0 4px 10px rgba(239, 68, 68, 0.15); transform: translateY(-3px); }
                .opinion-box.active-d { border-color: #f59e0b; background: #fffbeb; box-shadow: 0 4px 10px rgba(245, 158, 11, 0.15); transform: translateY(-3px); }
                .opinion-box.active-c { border-color: #64748b; background: #f8fafc; box-shadow: 0 4px 10px rgba(100, 116, 139, 0.15); transform: translateY(-3px); }

                /* Mobile Input */
                .mobile-input-wrapper { display: flex; align-items: center; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; background: #f8fafc; height: 50px; }
                .prefix-box { background: white; height: 100%; padding: 0 16px; display: flex; align-items: center; border-right: 1px solid #e2e8f0; color: #1e293b; }
                .mobile-input-wrapper input { font-size: 14px; padding: 0 16px; font-weight: 500; }

                /* Textarea & Select */
                .custom-textarea { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; font-size: 14px; min-height: 120px; resize: none; box-shadow: none; }
                .custom-textarea:focus { border-color: #3b82f6; background: white; }
                
                .custom-select { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 0 16px; font-size: 14px; height: 50px; box-shadow: none; font-weight: 500; color: #475569; }
                .custom-select:focus { border-color: #3b82f6; background: white; color: #1e293b; }

                /* Save Button */
                .btn-save-survey { width: 100%; background: #1e293b; color: white; border: none; padding: 16px; border-radius: 16px; font-size: 16px; font-weight: 700; cursor: pointer; transition: all 0.2s; box-shadow: 0 4px 12px rgba(30, 41, 59, 0.2); }
                .btn-save-survey:hover { background: #0f172a; transform: translateY(-2px); }
                .btn-save-survey:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }

                /* Animations */
                .slide-up { animation: slideUp 0.4s ease forwards; opacity: 0; }
                .fade-in { animation: fadeIn 0.3s ease forwards; }
                @keyframes slideUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

                /* Mobile Adjustments */
                @media (max-width: 480px) {
                    .opinion-grid { gap: 8px; }
                    .opinion-box { padding: 12px 4px; }
                    .opinion-box svg { width: 26px; height: 26px; }
                    .opinion-box span { font-size: 10px; }
                }
            `}</style>
        </div>
    );
};

export default VoterSurveyForm;